<script setup>
import { onMounted, onUnmounted } from 'vue'
import Loading from 'vue-loading-overlay'

import ImoConfirm from '@/components/modal/ImoConfirm.vue'
import ImoModalManager from '@/components/modal/ImoModalManager.vue'
import SidePanelManager from '@/components/sidepanel/SidePanelManager.vue'
import { useHotjar } from '@/composables/useHotjar'
import { useLoadStore } from '@/stores/Loader'

const loadStore = useLoadStore()

/**
 * Publica a altura REALMENTE visível da janela em `--app-height`.
 *
 * `100vh` no Safari do iOS/iPadOS mede o viewport grande — o da tela cheia, como
 * se as barras do navegador não existissem. Um painel de altura cheia fica então
 * mais alto que a área visível, e o rodapé (com os botões de ação) cai fora da
 * tela. Foi o bug 86akhkqt2: a cliente não via o "Continuar" da transferência.
 *
 * `window.innerHeight` devolve a altura visível em qualquer navegador. O CSS usa
 * esta variável como base e `dvh` por cima: quem entende `dvh` (Safari 15.4+,
 * Chrome 108+) sobrescreve, quem não entende descarta a linha e fica com ela.
 *
 * Lemos sempre `window.innerHeight`, e não `visualViewport.height`, para manter a
 * mesma semântica do `dvh`: altura sem as barras do navegador, ignorando o
 * teclado virtual. Assim o fallback e o `dvh` não divergem entre navegadores.
 */
const setAppHeight = () => {
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`)
}

onMounted(() => {
  useHotjar()

  setAppHeight()
  window.addEventListener('resize', setAppHeight)
  // iOS antigo não dispara `resize` ao girar o aparelho.
  window.addEventListener('orientationchange', setAppHeight)
  // `orientationchange` dispara ANTES do reflow no iOS: ler `innerHeight` ali
  // devolve a altura da orientação anterior. O `resize` do `visualViewport` vem
  // depois que o viewport assenta, então corrige a leitura — e é também o único
  // evento confiável quando a barra de endereço aparece ou some durante o scroll.
  // Existe desde o iOS 13; abaixo disso resta o `orientationchange` sozinho.
  window.visualViewport?.addEventListener('resize', setAppHeight)
})

onUnmounted(() => {
  window.removeEventListener('resize', setAppHeight)
  window.removeEventListener('orientationchange', setAppHeight)
  window.visualViewport?.removeEventListener('resize', setAppHeight)
})
</script>

<template>
  <div id="app">
    <Loading
      v-if="loadStore.loading"
      :active="loadStore.loading"
      :can-cancel="false"
      :is-full-page="true"
      color="#FFF"
      background-color="#000"
      :opacity="0.8"
      loader="spinner"
      :width="80"
      :height="80"
    />
    <router-view />
    <div id="modalsrender" />

    <!-- SidePanel Manager Global -->
    <SidePanelManager />

    <!-- Modal de confirmação global -->
    <ImoConfirm />

    <!-- Modal Manager global -->
    <ImoModalManager />
  </div>
</template>

<style>
.vl-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
